// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import PropTypes from 'prop-types';

// Data
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useRef, useState } from "react";

// API/MODELS
import groupAPI from "services/api/groups";
import clearwaysAPI from "modules/clearways/api";

// Mapbox
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helper
import ClearwaysSidebar from "components/Sidenavs/ClearwaysSidebar";
import { processGroupData } from "utils/group";
import { processEventsData } from "utils/event";
import { useSoftUIController, setMapDevices } from "hooks/context";
import { enableMap } from "utils/map";
import ClearwaysGroupsMap from "components/Maps/ClearwaysMaps/GroupsMap";


mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function ClearwaysGroupsMapView({ map, user, devices, selectedViewDevice }) {
    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout, miniNavbar, selectedOrg } = controller;

    useEffect(() => {
        if (!map.current) return;
        enableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    useEffect(() => {
        if(selectedViewDevice){
            setMapDevices(dispatch, true);
        }
    }, [selectedViewDevice])

    groupAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());

    const [groupPreviewID, setGroupPreviewID] = useState(0);
    const [hasFittedBounds, setHasFittedBounds] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);

    const set_padding = 120;
    const getMapPadding = () => ({
        left: deviceLayout === 'mobile' ? set_padding / 2 : /*(miniNavbar ? 0 : 450)*/ 0 + set_padding,
        right: deviceLayout === 'mobile' ? set_padding / 2 : set_padding,
        top: deviceLayout === 'mobile' ? set_padding / 2 : 50 + set_padding,
        bottom: deviceLayout === 'mobile' ? set_padding / 2 : set_padding
    });

    const groups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const events = useQuery({
        queryKey: ['getAllClearways', selectedOrg],
        queryFn: async () => {
            return await clearwaysAPI.getAllClearways();
        }
    });

    const events_data = useMemo(() => {
        return events.data ? processEventsData(events.data) : []
    }, [events.data]);

    const groups_data = useMemo(() => {
        if (typeof groups.data != 'undefined') {
            var groups_data = groups.data.filter((row) => row.type == "clearways").map(function (group) {
                var ret = processGroupData(groups.dataUpdatedAt, group, devices.data);
                return ret;
            }).filter(group => typeof group.parent_id == 'undefined');

            return groups_data;
        }

        return null;
    }, [groups.data, devices.data]);

    useEffect(() => {
        setHasFittedBounds(false);
        setSelectedDevice(null);
    }, [selectedOrg]);

    useEffect(() => {
        if (!map.current || !groups.data) return;

        // SCENARIO 1: A specific device is selected -> Center/fly to that device
        if (selectedViewDevice) {
            map.current.flyTo({
                center: [selectedViewDevice.position[0], selectedViewDevice.position[1]],
                zoom: 18.5,
                essential: true,
                speed: 2,
                padding: getMapPadding()
            });

            setSelectedDevice(selectedViewDevice);
            setHasFittedBounds(true);
            return;
        }

        var bounds = new mapboxgl.LngLatBounds();
        let hasCoords = false;

        groups_data.forEach(function (group) {
            group.geojson.forEach(function (feature) {
                feature.geometry.coordinates.forEach(function (coords) {
                    coords.forEach(function (coord) {
                        if (coord[0] !== 0 && coord[1] !== 0) {
                            bounds.extend(coord);
                            hasCoords = true;
                        }
                    })
                })
            })
        });

        if (hasCoords) {
            var options = { padding: getMapPadding(), linear: true, duration: 800 };
            if (hasFittedBounds) {
                options.duration = 300;
            }

            map.current.fitBounds(bounds, options);
            setHasFittedBounds(true);
        }
    }, [map, groups_data, deviceLayout]);

    useEffect(() => {
        if (!map.current || !hasFittedBounds) return;

        map.current.easeTo({
            padding: getMapPadding(),
            duration: 800,
            essential: true
        });
    }, [miniNavbar, deviceLayout]);

    if (groups.isLoading || events.isLoading) {
        return ('Loading...');
    }

    if (groups.error || events.error) {
        return ("An error has occurred");
    }

    if (!groups.data) {
        return ('Loading...');
    }

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            <ClearwaysGroupsMap {...{ map, user, groups_data, setGroupPreviewID, setSelectedDevice }} />
            <ClearwaysSidebar
                events={events_data}
                {...{ user, groupPreviewID, devices, selectedDevice, setSelectedDevice }}
            />
        </SoftBox>
    );
}

ClearwaysGroupsMapView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    devices: PropTypes.object,
    selectedViewDevice: PropTypes.object
}

export default ClearwaysGroupsMapView;